import { User as ConfigCatUser } from 'configcat-common'
import * as configcat from 'configcat-js'
import * as React from 'react'
import { useAnalytics } from './AnalyticsProvider'
import { useTrackingContext } from './TrackingProvider'
import { FeatureFlagValue, IndentifiableUser } from './types'

/**
 * Add string literals for all feature flag keys that are used in this application
 */
type SupportedFeatureFlag = never

const LOG_LEVEL = 3 // Info
const logger =
  localStorage.getItem('featureFlagLog') === 'info' ? configcat.createConsoleLogger(LOG_LEVEL) : undefined

// These API keys are NOT SECRET, and are specific to this [<Product>, <Environment>], so no need to define
// them as global environment variables - they are intentionally included in application code
const CONFIGCAT_KEY = {
  development: 'c-fZCAZRUEKOAR2VARO9aA/9H5IS6c7YUG7TlT9O_e0kQ',
  production: 'c-fZCAZRUEKOAR2VARO9aA/ctAr313zGU-Is5ts60QoCg',
}

const env: keyof typeof CONFIGCAT_KEY = import.meta.env.PROD ? 'production' : 'development'
const key = CONFIGCAT_KEY[env]
console.debug('configcat', key)

const featureFlagClient = configcat.getClient(key, configcat.PollingMode.AutoPoll, { logger })

const DIGITS_RE = /^d+&/

/*
 * localStorage stores key:value pairs as strings - this function converts string values to
 * corresponding 'raw' JS values that may be used to override a feature flag value from
 * the external feature flag service
 */
const getOverride = (key: string): FeatureFlagValue => {
  const override = localStorage.getItem(key)
  switch (override) {
    case null:
    case '':
    case 'undefined':
      return undefined
    case 'true':
      return true
    case 'false':
      return false
    case 'null':
      return null
  }
  if (DIGITS_RE.test(override)) return parseInt(override, 10)
  return override
}

const userToIdentifier = (user?: IndentifiableUser | null): ConfigCatUser | undefined =>
  user?.id ? { identifier: user.id, custom: {} } : undefined

/**
 * The primary interface for feature flags, which are boolean values stored in an external
 * system that are generally used to indicate whether a particular feature/functionality is
 * 'enabled'.
 *
 * If the returned value is undefined, the feature flag has not been resolved yet.
 *
 * You can also provide your own feature flag value overrides using localStorage - use
 * the browser dev tools to set a key:value pair, where the key is the feature flag name
 * and the value is a raw JavaScript value (e.g. true, undefined, "my value" with quotes)
 *
 * @example
 *
 * const myFeatureIsEnabled = useFeatureFlag('myCoolFeature')
 *
 * if (typeof myFeatureIsEnabled === 'undefined') return null
 * if (myFeatureIsEnabled) return <MyCoolFeature />
 * return <div>My cool feature is not yet ready</div>
 *
 */
export const useFeatureFlag = (key: SupportedFeatureFlag) => {
  const analytics = useAnalytics()
  const trackedUser = analytics.getUser()
  const override = React.useMemo(() => getOverride(key), [key])
  const [flagValue, setFlagValue] = React.useState<FeatureFlagValue>(override)
  const [globalContext, setGlobalContextProperty] = useTrackingContext()

  // Sync evaluated flagValue with tracking context
  React.useEffect(() => {
    if (globalContext.flags && globalContext.flags[key] === flagValue) return
    setGlobalContextProperty('flags', { ...globalContext.flags, [key]: flagValue })
  }, [flagValue, globalContext, setGlobalContextProperty, key])

  React.useEffect(() => {
    if (override !== undefined) return // Don't fetch from external service if we have an override

    featureFlagClient.getValueAsync(key, false, userToIdentifier(trackedUser)).then((value) => {
      if (value === undefined && flagValue !== undefined) return
      if (value === flagValue) return
      setFlagValue(value)
    })
  }, [key, trackedUser, flagValue, override])

  return flagValue
}
